import { useCallback, useEffect, useState } from 'react';
import { useNotify } from 'react-admin';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { fromDateTimeLocalValue } from '../../shared/clientTime';
import { dateTimeNoSeconds } from '../../shared/dateTime';
import { SectionCard } from '../../shared/Panel';
import { useStillMounted } from '../../shared/recordScoped';
import { useFeature } from '../featureFlags/FeatureGate';
import {
  AVIZATION_CHANNELS,
  FEATURE_CLIENT_AVIZATIONS,
  FEATURE_CLIENT_UPDATES,
  MIN_REASON,
  RULE_POINTS,
  UPDATE_CATEGORIES,
  fetchRule,
  ruleSummary,
  saveRule,
  toErrorMessage,
  type RuleKind,
  type RuleView,
} from './clientCommunicationApi';

const TITLES: Record<RuleKind, string> = {
  avization: 'Reguła awizacji klienta (CS)',
  update: 'Reguła aktualizacji dla klienta (CS)',
};

const str = (v: unknown) => (typeof v === 'string' ? v : '');

interface RuleForm {
  channel: string;
  points: string;
  leadHours: string;
  contactName: string;
  contactPhone: string;
  contactEmail: string;
  categories: string[];
  owner: string;
  validFrom: string;
  reason: string;
}

/** Formularz z zapisanej reguły (bez reguły — wartości startowe do wyboru). */
function formFrom(v: RuleView | null): RuleForm {
  const c = (v?.rule?.config ?? {}) as Record<string, unknown>;
  return {
    channel: str(c.channel) || 'phone',
    points: str(c.points) || 'unloading',
    leadHours: typeof c.leadHours === 'number' ? String(c.leadHours) : '24',
    contactName: str(c.contactName),
    contactPhone: str(c.contactPhone),
    contactEmail: str(c.contactEmail),
    categories: Array.isArray(c.categories) ? (c.categories as string[]) : [],
    owner: v?.rule?.owner ?? '',
    validFrom: '',
    reason: '',
  };
}

/**
 * Reguła klienta dla CS na karcie kontrahenta (prace 26.10.2026, pakiet P1;
 * BR-CS-12 — reguła ma właściciela i datę; wzorzec reguły podwykonawcy
 * S-25.10-06): awizacje (kanał, punkty, ile godzin przed oknem, kontakt)
 * albo aktualizacje (o czym klient chce wiedzieć). Pusta, dopóki CS jej nie
 * wpisze — bez reguły awizacje zakłada CS ręcznie, a aktualizacje są
 * proponowane wszystkie z oznaczeniem. Powód obowiązkowy, historia zmian.
 */
export const ClientRuleCard = ({ contractorId, kind }: { contractorId: number; kind: RuleKind }) => {
  const notify = useNotify();
  const stillMounted = useStillMounted();
  const [view, setView] = useState<RuleView | null>(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<RuleForm>(() => formFrom(null));
  const [busy, setBusy] = useState(false);
  const p = `client-rule-${kind}`;

  const set = <K extends keyof RuleForm>(key: K) => (value: RuleForm[K]) =>
    setForm((f) => ({ ...f, [key]: value }));
  const { channel, points, leadHours, contactName, contactPhone, contactEmail, categories, owner, validFrom, reason } =
    form;
  const setChannel = set('channel');
  const setPoints = set('points');
  const setLeadHours = set('leadHours');
  const setContactName = set('contactName');
  const setContactPhone = set('contactPhone');
  const setContactEmail = set('contactEmail');
  const setOwner = set('owner');
  const setValidFrom = set('validFrom');
  const setReason = set('reason');
  const setCategories = (fn: (l: string[]) => string[]) => setForm((f) => ({ ...f, categories: fn(f.categories) }));

  const load = useCallback(() => {
    fetchRule(contractorId, kind)
      .then((v) => {
        if (!stillMounted()) return;
        setView(v);
        setForm(formFrom(v));
      })
      .catch(() => undefined);
  }, [contractorId, kind, stillMounted]);
  useEffect(load, [load]);

  if (!view) return null;

  const submit = async (remove: boolean) => {
    setBusy(true);
    try {
      const config = remove
        ? null
        : kind === 'avization'
          ? {
              channel,
              points,
              leadHours: Number(leadHours),
              ...(contactName.trim() ? { contactName } : {}),
              ...(contactPhone.trim() ? { contactPhone } : {}),
              ...(contactEmail.trim() ? { contactEmail } : {}),
            }
          : { categories };
      const next = await saveRule(contractorId, kind, {
        config,
        owner: owner.trim() || null,
        ...(validFrom ? { validFrom: fromDateTimeLocalValue(validFrom) ?? validFrom } : {}),
        reason,
      });
      setView(next);
      setForm(formFrom(next));
      setEditing(false);
      notify(remove ? 'Usunięto regułę klienta' : 'Zapisano regułę klienta', { type: 'success' });
    } catch (e) {
      notify(toErrorMessage(e), { type: 'error' });
    } finally {
      if (stillMounted()) setBusy(false);
    }
  };

  const rule = view.rule;
  return (
    <SectionCard title={TITLES[kind]}>
      <Box data-testid={kind === 'avization' ? 'client-rule-avization' : 'client-rule-update'}>
        {rule ? (
          <Typography variant="body2" data-testid={`${p}-summary`}>
            {ruleSummary(kind, rule.config)} · właściciel: {rule.owner ?? 'nieustalony'} · obowiązuje od{' '}
            {dateTimeNoSeconds(rule.validFrom)}
            {rule.inForce ? '' : ' (jeszcze nie obowiązuje)'}
          </Typography>
        ) : (
          <Alert severity="info" variant="outlined" data-testid={`${p}-empty`}>
            {kind === 'avization'
              ? 'Brak reguły awizacji — zadania awizacji zakłada CS przy zleceniu. Nie zgadujemy obowiązku klienta.'
              : 'Brak reguły — propozycje aktualizacji powstają przy każdym zdarzeniu, z oznaczeniem „bez reguły klienta”.'}
          </Alert>
        )}
        {view.canWrite && !editing ? (
          <Button size="small" sx={{ mt: 1 }} onClick={() => setEditing(true)} data-testid={`${p}-edit`}>
            {rule ? 'Zmień regułę' : 'Wpisz regułę'}
          </Button>
        ) : null}
        {editing ? (
          <Stack spacing={1} sx={{ mt: 1 }} data-testid={`${p}-form`}>
            {kind === 'avization' ? (
              <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 200px), 1fr))' }}>
                <TextField select size="small" label="Kanał" value={channel} onChange={(e) => setChannel(e.target.value)} slotProps={{ htmlInput: { 'data-testid': `${p}-channel` } }}>
                  {AVIZATION_CHANNELS.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.name}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField select size="small" label="Punkty" value={points} onChange={(e) => setPoints(e.target.value)} slotProps={{ htmlInput: { 'data-testid': `${p}-points` } }}>
                  {RULE_POINTS.map((r) => (
                    <MenuItem key={r.id} value={r.id}>
                      {r.name}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  size="small"
                  type="number"
                  label="Ile godzin przed oknem"
                  value={leadHours}
                  onChange={(e) => setLeadHours(e.target.value)}
                  slotProps={{ htmlInput: { min: 1, max: 72, 'data-testid': `${p}-lead` } }}
                />
                <TextField size="small" label="Kontakt — osoba (opcjonalnie)" value={contactName} onChange={(e) => setContactName(e.target.value)} slotProps={{ htmlInput: { maxLength: 200 } }} />
                <TextField size="small" label="Kontakt — telefon (opcjonalnie)" value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} slotProps={{ htmlInput: { maxLength: 100 } }} />
                <TextField size="small" label="Kontakt — e-mail (opcjonalnie)" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} slotProps={{ htmlInput: { maxLength: 320 } }} />
              </Box>
            ) : (
              <Box>
                <Typography variant="body2">Klient chce wiedzieć o:</Typography>
                {UPDATE_CATEGORIES.map((c) => (
                  <FormControlLabel
                    key={c.id}
                    data-testid={`${p}-category-${c.id}`}
                    control={
                      <Checkbox
                        size="small"
                        checked={categories.includes(c.id)}
                        onChange={(e) => setCategories((l) => (e.target.checked ? [...l, c.id] : l.filter((x) => x !== c.id)))}
                      />
                    }
                    label={c.name}
                  />
                ))}
              </Box>
            )}
            <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 220px), 1fr))' }}>
              <TextField size="small" label="Właściciel reguły" value={owner} onChange={(e) => setOwner(e.target.value)} helperText="pusty — nieustalony" slotProps={{ htmlInput: { maxLength: 255, 'data-testid': `${p}-owner` } }} />
              <TextField
                size="small"
                type="datetime-local"
                label="Obowiązuje od"
                value={validFrom}
                onChange={(e) => setValidFrom(e.target.value)}
                helperText="puste — od teraz"
                slotProps={{ inputLabel: { shrink: true }, htmlInput: { 'data-testid': `${p}-valid-from` } }}
              />
            </Box>
            <TextField
              size="small"
              label="Powód zmiany"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              helperText={`co najmniej ${MIN_REASON} znaków, np. „mail klienta z 26.10”`}
              slotProps={{ htmlInput: { maxLength: 500, 'data-testid': `${p}-reason` } }}
            />
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button size="small" variant="contained" disabled={busy || reason.trim().length < MIN_REASON} onClick={() => void submit(false)} data-testid={`${p}-save`}>
                Zapisz regułę
              </Button>
              {rule ? (
                <Button size="small" color="warning" disabled={busy || reason.trim().length < MIN_REASON} onClick={() => void submit(true)}>
                  Usuń regułę
                </Button>
              ) : null}
              <Button size="small" onClick={() => setEditing(false)}>
                Wróć
              </Button>
            </Box>
          </Stack>
        ) : null}
        {view.history.length > 0 ? (
          <Table size="small" sx={{ mt: 1 }} data-testid={`${p}-history`}>
            <TableHead>
              <TableRow>
                <TableCell>Kiedy</TableCell>
                <TableCell>Kto</TableCell>
                <TableCell>Było → jest</TableCell>
                <TableCell>Powód</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {view.history.map((h) => (
                <TableRow key={h.id}>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{dateTimeNoSeconds(h.changedAt)}</TableCell>
                  <TableCell>{h.changedByName ?? '—'}</TableCell>
                  <TableCell>
                    {ruleSummary(kind, h.oldConfig)} → {ruleSummary(kind, h.newConfig)}
                    {h.oldOwner !== h.newOwner ? ` · właściciel: ${h.oldOwner ?? 'nieustalony'} → ${h.newOwner ?? 'nieustalony'}` : ''}
                  </TableCell>
                  <TableCell>{h.reason}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : null}
      </Box>
    </SectionCard>
  );
};

/** Obie reguły na karcie kontrahenta — każda za swoim przełącznikiem. */
export const ClientCommunicationRules = ({ contractorId }: { contractorId: number }) => {
  const avizations = useFeature(FEATURE_CLIENT_AVIZATIONS);
  const updates = useFeature(FEATURE_CLIENT_UPDATES);
  return (
    <>
      {avizations ? <ClientRuleCard contractorId={contractorId} kind="avization" /> : null}
      {updates ? <ClientRuleCard contractorId={contractorId} kind="update" /> : null}
    </>
  );
};
